import React, { useState, useEffect } from 'react';

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  vermelho: '#e53935',
  borda:   '#e8eaed', cinzaBg: '#fafafa',
  texto:   '#1a1a2e', muted:   '#888',
  branco:  '#fff',
};

// Helpers de data ISO local pro input datetime-local
function nowLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

// ════════════════════════════════════════════════════════════════════════════
// AnotacaoModal — modal próprio pra criar uma anotação manual na timeline
// técnica do projeto.
//
// Campos:
//   • Texto da anotação (textarea, obrigatório)
//   • Data e hora do evento (datetime-local, default = agora)
//   • Exibir na timeline (checkbox, default true)
//
// Props:
//   aberto       : boolean
//   onCancelar   : callback (fecha sem salvar)
//   onConfirmar  : callback({ descricao, data_evento, exibir_timeline })
// ════════════════════════════════════════════════════════════════════════════
function AnotacaoModal({ aberto, onCancelar, onConfirmar }) {
  const [descricao,       setDescricao]      = useState('');
  const [dataEvento,      setDataEvento]     = useState(nowLocal());
  const [exibirTimeline,  setExibirTimeline] = useState(true);
  const [salvando,        setSalvando]       = useState(false);
  const [erro,            setErro]           = useState('');

  // Reseta ao abrir
  useEffect(() => {
    if (aberto) {
      setDescricao('');
      setDataEvento(nowLocal());
      setExibirTimeline(true);
      setErro('');
      setSalvando(false);
    }
  }, [aberto]);

  if (!aberto) return null;

  const confirmar = async () => {
    setErro('');
    if (!descricao.trim()) return setErro('A descrição é obrigatória.');

    setSalvando(true);
    try {
      await onConfirmar({
        descricao:       descricao.trim(),
        data_evento:     dataEvento,
        exibir_timeline: exibirTimeline,
      });
    } catch (err) {
      setErro(err?.response?.data?.mensagem || 'Erro ao salvar anotação.');
      setSalvando(false);
    }
  };

  // Z-index acima do PlanejamentoModal (10000)
  const Z_OVERLAY = 10010;
  const Z_MODAL   = 10011;

  return (
    <>
      <div onClick={onCancelar} style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: Z_OVERLAY,
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '92%', maxWidth: '520px',
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column',
        zIndex: Z_MODAL, overflow: 'hidden',
        fontFamily: "'Manrope', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${COR.borda}`,
          backgroundColor: COR.amarelo, color: COR.branco,
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span style={{ fontSize: '17px' }}>✏️</span>
          <span style={{ fontSize: '14px', fontWeight: '600', flex: 1 }}>
            Nova Anotação
          </span>
          <button onClick={onCancelar} style={{
            border: 'none', background: 'transparent', color: COR.branco,
            fontSize: '20px', cursor: 'pointer',
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 18px' }}>
          {/* Descrição */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block', fontSize: '11px', fontWeight: '600',
              color: COR.muted, marginBottom: '4px',
            }}>Descrição *</label>
            <textarea
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              placeholder="Ex: Reunião de kickoff agendada com o cliente para 15/05."
              rows={4}
              style={{
                width: '100%', padding: '8px 10px',
                fontSize: '13px', fontFamily: 'inherit',
                border: `1px solid ${COR.borda}`, borderRadius: '5px',
                color: COR.texto, resize: 'vertical', boxSizing: 'border-box',
              }}
              autoFocus
            />
          </div>

          {/* Data/hora do evento */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block', fontSize: '11px', fontWeight: '600',
              color: COR.muted, marginBottom: '4px',
            }}>Data e hora do evento</label>
            <input
              type="datetime-local"
              value={dataEvento}
              onChange={e => setDataEvento(e.target.value)}
              style={{
                width: '100%', padding: '8px 10px',
                fontSize: '13px', fontFamily: 'inherit',
                border: `1px solid ${COR.borda}`, borderRadius: '5px',
                color: COR.texto, boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Exibir na timeline */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            cursor: 'pointer', fontSize: '13px', color: COR.texto,
            padding: '8px 10px', borderRadius: '5px',
            backgroundColor: COR.cinzaBg,
            border: `1px solid ${COR.borda}`,
          }}>
            <input
              type="checkbox"
              checked={exibirTimeline}
              onChange={e => setExibirTimeline(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span>Exibir na timeline do projeto</span>
          </label>

          {/* Erro */}
          {erro && (
            <div style={{
              marginTop: '12px', padding: '8px 12px',
              backgroundColor: '#fee2e2', color: COR.vermelho,
              borderRadius: '5px', fontSize: '12px',
              border: `1px solid ${COR.vermelho}33`,
            }}>{erro}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 18px', borderTop: `1px solid ${COR.borda}`,
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
          backgroundColor: COR.cinzaBg,
        }}>
          <button onClick={onCancelar} disabled={salvando} style={{
            padding: '8px 18px', fontSize: '13px', fontWeight: '600',
            border: `1px solid ${COR.borda}`, borderRadius: '5px',
            backgroundColor: COR.branco, color: COR.texto,
            cursor: salvando ? 'not-allowed' : 'pointer',
          }}>Cancelar</button>
          <button onClick={confirmar} disabled={salvando || !descricao.trim()} style={{
            padding: '8px 22px', fontSize: '13px', fontWeight: '600',
            border: 'none', borderRadius: '5px',
            backgroundColor: (salvando || !descricao.trim()) ? '#cccccc' : COR.amarelo,
            color: COR.branco,
            cursor: (salvando || !descricao.trim()) ? 'not-allowed' : 'pointer',
          }}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </>
  );
}

export default AnotacaoModal;
